import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion,
  type Variants
} from 'framer-motion';
import { useId, useMemo } from 'react';
import { cn } from '@/lib/utils';

export type TextMorphProps = {
  children: string;
  as?: React.ElementType;
  className?: string;
  style?: React.CSSProperties;
  variants?: Variants;
  transition?: Transition;
};

// Hoisted to module scope so framer-motion sees referentially stable
// variant/transition objects across renders.
const DEFAULT_VARIANTS: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

const DEFAULT_TRANSITION: Transition = {
  type: 'spring',
  stiffness: 280,
  damping: 18,
  mass: 0.3
};

/**
 * Adapted from motion-primitives.com (MIT) —
 * https://motion-primitives.com/docs/text-morph
 */
export const TextMorph = ({
  children,
  as: Component = 'p',
  className,
  style,
  variants,
  transition
}: TextMorphProps) => {
  const uniqueId = useId();
  const reduceMotion = useReducedMotion();

  const characters = useMemo(() => {
    const charCounts: Record<string, number> = {};

    return children.split('').map(char => {
      const lowerChar = char.toLowerCase();
      charCounts[lowerChar] = (charCounts[lowerChar] || 0) + 1;

      return {
        id: `${uniqueId}-${lowerChar}${charCounts[lowerChar]}`,
        label: char === ' ' ? '\u00A0' : char
      };
    });
  }, [children, uniqueId]);

  if (reduceMotion) {
    // Bypass per-character animation entirely; render the string as-is so
    // users with vestibular-motion sensitivities don't see characters
    // shimmer in/out on every change.
    return (
      <Component className={cn(className)} style={style}>
        {children}
      </Component>
    );
  }

  return (
    <Component aria-label={children} className={cn(className)} style={style}>
      <AnimatePresence initial={false} mode="popLayout">
        {characters.map(character => (
          <motion.span
            animate="animate"
            aria-hidden="true"
            className="inline-block"
            exit="exit"
            initial="initial"
            key={character.id}
            layoutId={character.id}
            transition={transition ?? DEFAULT_TRANSITION}
            variants={variants ?? DEFAULT_VARIANTS}
          >
            {character.label}
          </motion.span>
        ))}
      </AnimatePresence>
    </Component>
  );
};
